import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import PageHeader from "../components/common/PageHeader";
import FormField from "../components/common/FormField";
import { useControlCash, CURRENCY_OPTIONS } from "../store/controlCashStore";
import { useToast } from "../contexts/ToastContext";
import { getTodayDate } from "../utils/common";

const CurrencyConversion = () => {
  const { id } = useParams(); // Para modo edición
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state, dispatch, formatDisplayAmount } = useControlCash();
  const toast = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [monthId, setMonthId] = useState(null);
  const [formData, setFormData] = useState({
    fromCurrency: "USD",
    fromAmount: "",
    toCurrency: "PEN",
    toAmount: "",
    exchangeRate: "",
    date: getTodayDate(),
    note: ""
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const isEditMode = !!id;

  // Obtener monthId y datos iniciales
  useEffect(() => {
    if (isEditMode && id) {
      // Modo edición: cargar conversión existente
      // Buscar en todos los meses (pasar null para buscar en todos)
      const allConversions = state.currencyConversions || [];
      const conversion = allConversions.find(c => c.id === id);
      if (conversion) {
        setMonthId(conversion.monthId);
        setFormData({
          fromCurrency: conversion.fromCurrency || "USD",
          fromAmount: conversion.fromAmount.toString(),
          toCurrency: conversion.toCurrency || "PEN",
          toAmount: conversion.toAmount.toString(),
          exchangeRate: conversion.exchangeRate.toString(),
          date: conversion.date || getTodayDate(),
          note: conversion.note || ""
        });
      }
    } else {
      // Modo creación: obtener monthId de query params
      const monthIdParam = searchParams.get("monthId");
      setMonthId(monthIdParam);
      
      // Calcular tipo de cambio automático si hay tipos de cambio disponibles
      const rates = state.exchangeRates || {};
      if (formData.fromCurrency && formData.toCurrency && rates[formData.fromCurrency] && rates[formData.toCurrency]) {
        const fromRate = rates[formData.fromCurrency];
        const toRate = rates[formData.toCurrency];
        const calculatedRate = toRate / fromRate;
        setFormData(prev => ({ ...prev, exchangeRate: calculatedRate.toFixed(4) }));
      }
    }
  }, [id, isEditMode, searchParams, state.currencyConversions, state.exchangeRates]);

  // Calcular automáticamente el monto destino cuando cambia origen o tipo de cambio
  useEffect(() => {
    if (touched.fromAmount || touched.exchangeRate) {
      const fromAmt = parseFloat(formData.fromAmount);
      const rate = parseFloat(formData.exchangeRate);
      if (!isNaN(fromAmt) && !isNaN(rate) && rate > 0) {
        const calculated = fromAmt * rate;
        setFormData(prev => ({ ...prev, toAmount: calculated.toFixed(2) }));
      }
    }
  }, [formData.fromAmount, formData.exchangeRate, touched.fromAmount, touched.exchangeRate]);

  // Actualizar tipo de cambio cuando cambian las monedas
  useEffect(() => {
    const rates = state.exchangeRates || {};
    if (formData.fromCurrency && formData.toCurrency && rates[formData.fromCurrency] && rates[formData.toCurrency]) {
      const fromRate = rates[formData.fromCurrency];
      const toRate = rates[formData.toCurrency];
      const calculatedRate = toRate / fromRate;
      setFormData(prev => ({ ...prev, exchangeRate: calculatedRate.toFixed(4) }));
      
      // Recalcular monto destino si hay monto origen
      const fromAmt = parseFloat(formData.fromAmount);
      if (!isNaN(fromAmt) && fromAmt > 0) {
        const calculated = fromAmt * calculatedRate;
        setFormData(prev => ({ ...prev, toAmount: calculated.toFixed(2) }));
      }
    }
  }, [formData.fromCurrency, formData.toCurrency, state.exchangeRates]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fromAmount || parseFloat(formData.fromAmount) <= 0) {
      newErrors.fromAmount = "El monto origen debe ser mayor a 0";
    }

    if (!formData.toAmount || parseFloat(formData.toAmount) <= 0) {
      newErrors.toAmount = "El monto destino debe ser mayor a 0";
    }

    if (!formData.exchangeRate || parseFloat(formData.exchangeRate) <= 0) {
      newErrors.exchangeRate = "El tipo de cambio debe ser mayor a 0";
    }

    if (formData.fromCurrency === formData.toCurrency) {
      newErrors.toCurrency = "Debes seleccionar una moneda diferente";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Marcar todos los campos como tocados
    setTouched({
      fromAmount: true,
      toAmount: true,
      exchangeRate: true,
      toCurrency: true
    });

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    setTimeout(() => {
      if (isEditMode && id) {
        dispatch({
          type: "UPDATE_CURRENCY_CONVERSION",
          payload: {
            id,
            patch: {
              monthId: monthId,
              fromCurrency: formData.fromCurrency,
              fromAmount: parseFloat(formData.fromAmount),
              toCurrency: formData.toCurrency,
              toAmount: parseFloat(formData.toAmount),
              exchangeRate: parseFloat(formData.exchangeRate),
              date: formData.date,
              note: formData.note
            }
          }
        });
        console.log("✏️ Conversión actualizada:", id);
      } else {
        dispatch({
          type: "ADD_CURRENCY_CONVERSION",
          payload: {
            monthId: monthId,
            fromCurrency: formData.fromCurrency,
            fromAmount: parseFloat(formData.fromAmount),
            toCurrency: formData.toCurrency,
            toAmount: parseFloat(formData.toAmount),
            exchangeRate: parseFloat(formData.exchangeRate),
            date: formData.date,
            note: formData.note
          }
        });
        console.log("➕ Conversión creada");
      }

      setIsLoading(false);
      toast.success(`Conversión ${isEditMode ? "actualizada" : "registrada"} exitosamente!`);
      
      // Navegar de vuelta al detalle del mes o al dashboard
      if (monthId) {
        navigate(`/month/${monthId}`);
      } else {
        navigate("/");
      }
    }, 500);
  };

  const handleCancel = () => {
    if (monthId) {
      navigate(`/month/${monthId}`);
    } else {
      navigate("/");
    }
  };

  const enabledCurrencies = state.enabledCurrencies || ["PEN", "USD"];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader 
          isEditMode={isEditMode} 
          onBack={handleCancel}
          backAriaLabel="Volver"
        />

        {/* Card principal del formulario */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isEditMode ? "Editar conversión de moneda" : "Registrar conversión de moneda"}
          </h2>
          <p className="text-gray-600 mb-6">
            Registra cuando cambias dinero de una moneda a otra. Por ejemplo: cambiar 100 USD a 375 PEN.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Moneda origen y monto */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <FormField label="De moneda" required>
                  <div className="relative">
                    <select
                      value={formData.fromCurrency}
                      onChange={(e) => handleChange("fromCurrency", e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                      disabled={isLoading}
                    >
                      {CURRENCY_OPTIONS.filter(opt => enabledCurrencies.includes(opt.code)).map((opt) => (
                        <option key={opt.code} value={opt.code}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <svg 
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </FormField>
              </div>
              <div className="md:col-span-2">
                <FormField 
                  label="Monto origen" 
                  required 
                  error={touched.fromAmount && errors.fromAmount}
                >
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                      {CURRENCY_OPTIONS.find(opt => opt.code === formData.fromCurrency)?.symbol || "$"}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.fromAmount}
                      onChange={(e) => handleChange("fromAmount", e.target.value)}
                      onBlur={() => handleBlur("fromAmount")}
                      placeholder="0.00"
                      className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                        touched.fromAmount && errors.fromAmount
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                      }`}
                      disabled={isLoading}
                    />
                  </div>
                </FormField>
              </div>
            </div>

            {/* Tipo de cambio */}
            <FormField 
              label="Tipo de cambio" 
              required 
              helperText={`1 ${formData.fromCurrency} = ${formData.exchangeRate || "?"} ${formData.toCurrency}`}
              error={touched.exchangeRate && errors.exchangeRate}
            >
              <input
                type="number"
                step="0.0001"
                min="0"
                value={formData.exchangeRate}
                onChange={(e) => handleChange("exchangeRate", e.target.value)}
                onBlur={() => handleBlur("exchangeRate")}
                placeholder="Ej: 3.75"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                  touched.exchangeRate && errors.exchangeRate
                    ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                }`}
                disabled={isLoading}
              />
            </FormField>

            {/* Moneda destino y monto */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <FormField label="A moneda" required error={touched.toCurrency && errors.toCurrency}>
                  <div className="relative">
                    <select
                      value={formData.toCurrency}
                      onChange={(e) => handleChange("toCurrency", e.target.value)}
                      onBlur={() => handleBlur("toCurrency")}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none bg-white cursor-pointer ${
                        touched.toCurrency && errors.toCurrency
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                      }`}
                      disabled={isLoading}
                    >
                      {CURRENCY_OPTIONS.filter(opt => enabledCurrencies.includes(opt.code)).map((opt) => (
                        <option key={opt.code} value={opt.code}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <svg 
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {touched.toCurrency && errors.toCurrency && (
                    <p className="mt-1 text-sm text-red-600">{errors.toCurrency}</p>
                  )}
                </FormField>
              </div>
              <div className="md:col-span-2">
                <FormField 
                  label="Monto destino" 
                  required 
                  error={touched.toAmount && errors.toAmount}
                  helperText="Se calcula automáticamente según el tipo de cambio"
                >
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                      {CURRENCY_OPTIONS.find(opt => opt.code === formData.toCurrency)?.symbol || "S/"}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.toAmount}
                      onChange={(e) => handleChange("toAmount", e.target.value)}
                      onBlur={() => handleBlur("toAmount")}
                      placeholder="0.00"
                      className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                        touched.toAmount && errors.toAmount
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                      }`}
                      disabled={isLoading}
                    />
                  </div>
                </FormField>
              </div>
            </div>

            {/* Fecha */}
            <FormField label="Fecha" required>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange("date", e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all"
                disabled={isLoading}
              />
            </FormField>

            {/* Nota (opcional) */}
            <FormField label="Nota" helperText="Agrega detalles adicionales (opcional)">
              <textarea
                value={formData.note}
                onChange={(e) => handleChange("note", e.target.value)}
                placeholder="Ej: Cambio realizado en casa de cambio, incluye comisión..."
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all resize-none"
                disabled={isLoading}
              />
            </FormField>

            {/* Resumen de la conversión */}
            {formData.fromAmount && formData.toAmount && !errors.fromAmount && !errors.toAmount && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">Resumen de la conversión:</p>
                <div className="flex items-center gap-2 text-lg font-bold text-blue-700">
                  <span>{formatDisplayAmount(parseFloat(formData.fromAmount), false, formData.fromCurrency)}</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  <span>{formatDisplayAmount(parseFloat(formData.toAmount), false, formData.toCurrency)}</span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Tipo de cambio: 1 {formData.fromCurrency} = {parseFloat(formData.exchangeRate).toFixed(4)} {formData.toCurrency}
                </p>
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t-2 border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Cancelar y volver"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                aria-label="Guardar conversión"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Guardando...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    {isEditMode ? "Actualizar conversión" : "Registrar conversión"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CurrencyConversion;
